import { Link } from "@tanstack/react-router";
import { useAuth } from "contexts/AuthContext";
import { ChevronsUpDown, LogOut } from "lucide-react";
import { t } from "i18next";
import { useEffect, useRef } from "react";
import { useLayout } from "@/contexts/LayoutContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/button/Button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext";

export function Navbar() {
  const { authUser, signOut, isLoggedIn, canSeePrototypes } = useAuth();
  const { features } = useFeatureFlags();
  const { setNavbarHeightPx } = useLayout();
  const navbarRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const element = navbarRef.current;
    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }
    const updateHeight = () => {
      setNavbarHeightPx(element.offsetHeight);
    };
    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(element);
    return () => {
      resizeObserver.disconnect();
      setNavbarHeightPx(0);
    };
  }, [setNavbarHeightPx, features]);

  if (!isLoggedIn) return null;

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <header
      ref={navbarRef}
      className="fixed z-50 w-full bg-secondary-950 border-b border-secondary-800"
    >
      <div className="flex items-center justify-between min-h-[3rem] py-[0.5rem] px-[1rem]">
        <div className="flex items-center gap-6">
          <Link
            to="/home"
            className="text-xl font-normal text-foreground"
            search={{
              redirect: window.location.pathname,
            }}
          >
            {t("general:logoText")}
          </Link>
          {canSeePrototypes && (
            <Link
              to="/prototypes"
              className="text-sm text-secondary-300 hover:text-white [&.active]:text-white"
            >
              Prototypes
            </Link>
          )}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex items-center space-x-2 px-[0.5rem] border-white shadow-none focus:outline-none"
            >
              <Avatar className="h-8 w-8">
                <AvatarImage
                  src={authUser?.photoURL ?? undefined}
                  alt={authUser?.displayName ?? ""}
                />
                <AvatarFallback>
                  {authUser?.email?.[0].toUpperCase() ?? "?"}
                </AvatarFallback>
              </Avatar>
              <div className="text-left">
                <span className="hidden text-sm font-medium text-sidebar-foreground sm:block">
                  {authUser?.displayName}
                </span>
                <span className="hidden text-xs font-light text-sidebar-foreground sm:block">
                  {authUser?.email}
                </span>
              </div>
              <ChevronsUpDown className="h-[1rem] w-[1rem] text-sidebar-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            sizeToTrigger
            align="start"
            className=""
            forceMount
          >
            <Button
              onClick={handleSignOut}
              variant="ghost"
              icon={<LogOut className="w-[1rem] h-[1rem]" />}
              className="w-full justify-start rounded-md font-normal py-[0.625rem] px-[0.5rem]"
              label={t("general:buttons:logout")}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {features.infoBannerText && (
        <div className="py-[0.5rem] px-[0.25rem] bg-primary text-white text-center text-sm content-around whitespace-pre-line">
          <p>{features.infoBannerText}</p>
        </div>
      )}
    </header>
  );
}
